import Button from '@components/Button'
import NFTDisplayer from '@components/NftDisplayer'
import useBalances from '@plug-core/hooks/useBalances'
import { Avatar, Box, Chip, Divider, Stack, Typography } from '@mui/material'
import { ROUTES } from '@utils/routes'
import React, { useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { HiArrowTopRightOnSquare } from 'react-icons/hi2'
import { IoIosSend } from 'react-icons/io'
import { BTCNFT, ICNFT, NFT } from '@plug-core/interfaces/common'
import { useTranslation } from 'react-i18next'
import { BounceContainer } from '@components/BounceContainer/BounceContainer'

interface InfoChipProps {
  label: string
  value?: string
  imgSrc?: string
}

const InfoChip = ({ label, value, imgSrc }: InfoChipProps) => (
  <BounceContainer>
    <Stack
      direction="row"
      borderRadius={2}
      height={36}
      px={1.5}
      border={1}
      bgcolor={(theme) => theme.palette.background.paper}
      alignItems="center"
      spacing={1}
    >
      {imgSrc && (
        <Avatar
          src={imgSrc}
          sx={{
            width: 24,
            height: 24,
          }}
        />
      )}
      <Typography>{label}</Typography>
      <Typography color={(theme) => theme.palette.text.secondary}>{value}</Typography>
    </Stack>
  </BounceContainer>
)

export const NftDetails = () => {
  const { nftCanisterId } = useParams<{ nftCanisterId: string }>()

  const { t } = useTranslation()

  const {
    state: { item },
  } = useLocation() as { state: { item: ICNFT | BTCNFT } }

  const navigate = useNavigate()
  const { walletNfts } = useBalances()

  const collection = useMemo(() => {
    return walletNfts?.find((collection) => collection.canisterId === nftCanisterId)
  }, [nftCanisterId])

  const nftProperties = useMemo(() => {
    let properties: { label: string; value?: string; img?: string }[] = []

    if (collection) {
      properties.push({ label: collection.name, img: collection.icon })
    }

    if (item.standard === 'ordinal') {
      properties.push({ label: 'ID', value: item.number.toString() })
      properties.push({ label: t('nft.standard'), value: item.standard })
      properties.push({
        label: t('nft.inscribed'),
        value: item.genesisTimestamp ? new Date(item.genesisTimestamp).toLocaleString() : 'N/A',
      })
      properties.push({ label: t('nft.rarity'), value: item.satRarity })
    } else {
      properties.push({ label: 'ID', value: item.index.toString() })
      properties.push({ label: t('nft.standard'), value: item.standard })
    }

    return properties
  }, [collection, item])

  const nftMetadata = useMemo(() => {
    if (item.standard !== 'ordinal') return []

    return Object.entries(item.metadata || {})
  }, [item])

  return (
    <Stack spacing={2} px={2}>
      {item && <NFTDisplayer nft={item} />}
      <Stack spacing={1} direction="row">
        <Button
          variant="contained"
          startIcon={<HiArrowTopRightOnSquare size={20} />}
          onClick={() => window.open(item?.url, '_blank')}
          fullWidth
        >
          {t('nft.openAsset')}
        </Button>
        <Button
          variant="rainbow"
          startIcon={<IoIosSend size={20} />}
          onClick={() => navigate(ROUTES.SEND, { state: { selectedNft: item } })}
          fullWidth
        >
          {t('nft.sendNft')}
        </Button>
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="h6">{collection ? t('nft.collection') : t('nft.info')}</Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {nftProperties.map(({ label, value, img }) => (
            <InfoChip key={label} label={label} value={value} imgSrc={img} />
          ))}
        </Stack>
      </Stack>

      <Box py={1}>
        <Divider sx={{ borderColor: '#23262F' }} />
      </Box>

      <Stack spacing={1}>
        {collection?.description && (
          <>
            <Typography variant="h6">{t('nft.about')}</Typography>
            <Typography variant="body2">{collection.description}</Typography>
          </>
        )}
        {nftMetadata.length > 0 && (
          <Stack spacing={0.75}>
            {nftMetadata.map(([key, value]) => (
              <Stack direction="row" spacing={1} key={key}>
                <Typography variant="body2" fontWeight="bold">
                  {key}:
                </Typography>
                <Typography variant="body2">{value}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>

      <Box height={24} />
    </Stack>
  )
}
